import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot, LayoutGrid, Settings2, Database, Boxes, Code2, Inbox, Play,
  Plus, LogOut, Moon, Sun, ChevronDown, Trash2, Building2, SlidersHorizontal, Star, ScrollText, DatabaseBackup,
  UsersRound, MessagesSquare, Activity,
} from "lucide-react";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

import Overview from "@/components/sections/Overview";
import BusinessManager from "@/components/sections/BusinessManager";
import ConfigStudio from "@/components/sections/ConfigStudio";
import DatabaseHub from "@/components/sections/DatabaseHub";
import Catalog from "@/components/sections/Catalog";
import EmbedStudio from "@/components/sections/EmbedStudio";
import LeadsInbox from "@/components/sections/LeadsInbox";
import WidgetPlayground from "@/components/sections/WidgetPlayground";
import ActivityLog from "@/components/sections/ActivityLog";
import BackupRestore from "@/components/sections/BackupRestore";
import UserManager from "@/components/sections/UserManager";
import ConversationHistory from "@/components/sections/ConversationHistory";
import LlmUsage from "@/components/sections/LlmUsage";
import Settings from "@/pages/Settings";

const TABS = [
  { id: "overview", label: "Ringkasan", icon: LayoutGrid },
  { id: "manage", label: "Kelola Bisnis", icon: Building2 },
  { id: "config", label: "Konfigurasi Chatbot", icon: Settings2 },
  { id: "database", label: "Database", icon: Database },
  { id: "catalog", label: "Katalog & Pesanan", icon: Boxes },
  { id: "embed", label: "Script Embed", icon: Code2 },
  { id: "leads", label: "Leads & WhatsApp", icon: Inbox },
  { id: "conversations", label: "Riwayat Percakapan", icon: MessagesSquare },
  { id: "playground", label: "Widget Playground", icon: Play },
  { id: "usage", label: "Monitoring AI", icon: Activity },
  { id: "activity", label: "Log Aktivitas", icon: ScrollText },
  { id: "backup", label: "Backup & Restore", icon: DatabaseBackup },
  { id: "users", label: "Kelola User", icon: UsersRound },
  { id: "settings", label: "Settings", icon: SlidersHorizontal },
];

const ROLE_TABS = {
  super_admin: TABS.map((t) => t.id),
  business_admin: ["overview", "leads", "conversations"],
  operator: ["overview", "conversations", "settings"],
};

// Ingat bisnis terakhir yang dibuka (per akun) agar refresh/login tidak selalu
// kembali ke bisnis pertama di list. Disimpan per user id supaya tidak bentrok
// bila beberapa akun memakai browser yang sama.
const lastBusinessKey = (uid) => `veloce:lastBusiness:${uid || "anon"}`;
const readLastBusiness = (uid) => {
  try { return localStorage.getItem(lastBusinessKey(uid)); } catch { return null; }
};
const saveLastBusiness = (uid, id) => {
  try { localStorage.setItem(lastBusinessKey(uid), id); } catch {}
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  // Mulai dari bisnis terakhir yang tersimpan (bila ada); validasi dilakukan setelah load.
  const [activeId, setActiveId] = useState(() => readLastBusiness(user?.id));
  const [tab, setTab] = useState("overview");
  const [dark, setDark] = useState(false);
  const [newName, setNewName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const active = businesses.find((b) => b.id === activeId);

  const role = (user && user.role) || "business_admin";
  const allowedIds = ROLE_TABS[role] || ROLE_TABS.business_admin;
  const visibleTabs = TABS.filter((t) => allowedIds.includes(t.id));

  useEffect(() => {
    if (!allowedIds.includes(tab)) setTab(visibleTabs[0]?.id || "settings");
  }, [role]); // eslint-disable-line

  const loadBusinesses = async () => {
    const { data } = await api.get("/businesses");
    setBusinesses(data);
    if (!data.length) { setActiveId(null); return; }
    // Prioritas: pilihan saat ini → bisnis terakhir di localStorage (bila masih ada) → bisnis pertama.
    const saved = readLastBusiness(user?.id);
    const next = data.find((b) => b.id === activeId)?.id
      || (saved && data.find((b) => b.id === saved)?.id)
      || data[0].id;
    setActiveId(next);
  };

  useEffect(() => { loadBusinesses(); }, []);

  // Simpan setiap perubahan bisnis aktif (klik dropdown, tombol Buka, buat/hapus bisnis).
  useEffect(() => {
    if (activeId) saveLastBusiness(user?.id, activeId);
  }, [activeId, user?.id]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const updateBusiness = (updated) =>
    setBusinesses((bs) => bs.map((b) => (b.id === updated.id ? updated : b)));

  const createBusiness = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/businesses", { name: newName.trim() });
      await loadBusinesses();
      setActiveId(data.id);
      setNewName("");
      setDialogOpen(false);
      toast.success("Bisnis dibuat");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteBusiness = async (id) => {
    try {
      await api.delete(`/businesses/${id}`);
      toast.success("Bisnis dihapus");
      const remaining = businesses.filter((b) => b.id !== id);
      setBusinesses(remaining);
      setActiveId(remaining[0]?.id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  // A brand-new Super Admin with no business yet gets the "create first business" screen.
  if (!active && role === "super_admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Bot className="w-10 h-10 text-accent" />
        <p className="text-muted-foreground">Belum ada bisnis. Buat bisnis pertamamu.</p>
        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nama bisnis"
            data-testid="empty-business-name" className="w-56" />
          <Button onClick={createBusiness} data-testid="empty-create-business" className="bg-accent text-white">
            <Plus className="w-4 h-4 mr-1" /> Buat
          </Button>
        </div>
      </div>
    );
  }

  const noBusiness = !active;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center shrink-0">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold tracking-tight hidden sm:block">Veloce</span>

            <div className="h-6 w-px bg-border mx-1 hidden sm:block" />

            {active && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="business-switcher-select" className="gap-2 max-w-[220px]">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: active.config.accent_color }} />
                    <span className="truncate">{active.name}</span>
                    <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  {businesses.map((b) => (
                    <DropdownMenuItem key={b.id} data-testid="business-card-item"
                      onClick={() => setActiveId(b.id)} className="gap-2 group">
                      <span className="w-2 h-2 rounded-full" style={{ background: b.config.accent_color }} />
                      <span className="truncate flex-1">{b.name}</span>
                      {b.is_default && <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                      {businesses.length > 1 && !b.is_default && role === "super_admin" && (
                        <Trash2 className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); deleteBusiness(b.id); }} />
                      )}
                    </DropdownMenuItem>
                  ))}
                  {role === "super_admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                          <DropdownMenuItem data-testid="btn-create-business" onSelect={(e) => e.preventDefault()} className="gap-2 text-accent">
                            <Plus className="w-4 h-4" /> Bisnis Baru
                          </DropdownMenuItem>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Buat Bisnis Baru</DialogTitle></DialogHeader>
                          <div className="space-y-2">
                            <Label>Nama Bisnis</Label>
                            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                              data-testid="input-new-business-name" placeholder="mis. Kopi Nusantara" />
                          </div>
                          <DialogFooter>
                            <Button onClick={createBusiness} data-testid="submit-new-business" className="bg-accent text-white">Buat</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="flex items-center gap-2">
            {active && (
              <Badge variant="outline" className="hidden md:flex font-mono text-[11px] uppercase">
                {active.db_config.type}
              </Badge>
            )}
            <Button size="icon" variant="ghost" onClick={() => setDark(!dark)} data-testid="theme-toggle">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu">
                  <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                    {(user?.name || "A")[0].toUpperCase()}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-destructive gap-2">
                  <LogOut className="w-4 h-4" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden lg:flex flex-col w-60 border-r border-border min-h-[calc(100vh-4rem)] p-3 gap-1 sticky top-16">
          {visibleTabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`nav-${t.id}`}
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${
                tab === t.id ? "text-accent" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}>
              {tab === t.id && (
                <motion.div layoutId="active-tab" className="absolute inset-0 rounded-xl bg-accent/10 border border-accent/20" />
              )}
              <t.icon className="w-4 h-4 relative z-10" />
              <span className="relative z-10">{t.label}</span>
            </button>
          ))}
        </aside>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-3 mb-2">
            {visibleTabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium ${tab === t.id ? "bg-accent text-white" : "bg-secondary text-muted-foreground"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              {TABS.find((t) => t.id === tab)?.label}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {tab === "settings" ? "Pengaturan akun & keamanan"
                : tab === "usage" ? "Pemakaian token & estimasi biaya LLM (monitoring & penagihan klien)"
                : tab === "activity" ? "Riwayat perubahan konfigurasi & database untuk audit"
                : tab === "backup" ? "Cadangkan & pulihkan data internal aplikasi"
                : tab === "manage" ? "Tambah, edit, hapus, dan konfigurasi database tiap bisnis"
                : active?.name}
            </p>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={tab + (active?.id || "none")} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {noBusiness && role !== "super_admin" && tab !== "settings" ? (
                <div className="rounded-2xl border border-border bg-card p-6 max-w-lg">
                  <Bot className="w-8 h-8 text-accent mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Belum ada bisnis yang ditugaskan kepada akun Anda. Hubungi Super Admin untuk mendapatkan akses.
                  </p>
                  <Button variant="outline" className="mt-4" onClick={() => setTab("settings")}>
                    Buka Pengaturan
                  </Button>
                </div>
              ) : (
                <>
                  {active && tab === "overview" && <Overview business={active} />}
                  {active && tab === "manage" && <BusinessManager businesses={businesses} reload={loadBusinesses} onSelect={(id) => { setActiveId(id); setTab("overview"); }} currentUser={user} />}
                  {active && tab === "config" && <ConfigStudio business={active} onUpdated={updateBusiness} />}
                  {active && tab === "database" && <DatabaseHub business={active} onUpdated={updateBusiness} />}
                  {active && tab === "catalog" && <Catalog business={active} />}
                  {active && tab === "embed" && <EmbedStudio business={active} onUpdated={updateBusiness} />}
                  {active && tab === "leads" && <LeadsInbox business={active} />}
                  {active && tab === "conversations" && <ConversationHistory business={active} />}
                  {active && tab === "playground" && <WidgetPlayground business={active} />}
                  {tab === "usage" && <LlmUsage />}
                  {tab === "activity" && <ActivityLog />}
                  {tab === "backup" && <BackupRestore />}
                  {tab === "users" && <UserManager businesses={businesses} />}
                  {tab === "settings" && <Settings />}
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
